import React, { useState, useEffect } from "react";
import uiData from "./data.json";
import { api } from "../../api/apiConfig";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PartyList = () => {
  const [formData, setFormData] = useState({});
  const [parties, setParties] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState({
    opening: 0,
    debit: 0,
    credit: 0,
    amount: 0,
  });

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await api.get("/parties/list");
        setParties(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch parties:", err);
      }
    };
    fetchParties();
  }, []);

  const allTransactions = parties || [];

  const fetchTransactions = (updatedData) => {
    const dataToUse = updatedData || formData;
    const { ["Party Name"]: partyName, ["Date From"]: from, ["Date To"]: to } = dataToUse;

    const filteredTransactions = allTransactions.filter((t) => {
      const matchParty = partyName ? t.party_name === partyName : true;
      const matchFrom = from ? new Date(t.date) >= new Date(from) : true;
      const matchTo = to ? new Date(t.date) <= new Date(to) : true;
      return matchParty && matchFrom && matchTo;
    });
    console.log("filteredTransactions", filteredTransactions)
    setTransactions(filteredTransactions);

    const debit = filteredTransactions.reduce((s, r) => s + (r.debit || 0), 0);

    const credit = filteredTransactions.reduce((s, r) => s + (r.credit || 0), 0);
    const amount = debit - credit;

    setTotals({
      opening: filteredTransactions[0]?.opening_balance || 0,
      debit,
      credit,
      amount,
    });
  };

  const handleChange = (label, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [label]: value };

      if (label === "Party Name") {
        const selectedParty = parties.find((p) => p.party_name === value);
        updated["Party Code No"] = selectedParty ? selectedParty.party_code : "";
      }

      if (label === "Date From" && updated["Date To"]) {
        if (new Date(value) > new Date(updated["Date To"])) {
          alert("From Date cannot be later than To Date!");
          updated["Date From"] = "";
        }
      } else if (label === "Date To" && updated["Date From"]) {
        if (new Date(value) < new Date(updated["Date From"])) {
          alert("To Date cannot be earlier than From Date!");
          updated["Date To"] = "";
        }
      }

      if (["Party Name", "Date From", "Date To"].includes(label)) {
        fetchTransactions(updated);
      }

      return updated;
    });
  };

  const exportToExcel = () => {
    if (transactions.length === 0) {
      alert("No data to export!");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(transactions);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Party Report");

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Party_Report_${formData["Party Name"] || "All"}_${new Date()
        .toISOString()
        .split("T")[0]}.xlsx`
    );
  };

  const exportWeeklyReport = () => {
    if (transactions.length === 0) {
      alert("No data to export!");
      return;
    }

    const weekGroups = {};
    transactions.forEach((t) => {
      const week = new Date(t.date);
      const weekNum = Math.ceil(week.getDate() / 7);
      if (!weekGroups[weekNum]) weekGroups[weekNum] = [];
      weekGroups[weekNum].push(t);
    });

    const weeklySummary = Object.keys(weekGroups).map((week) => {
      const weekData = weekGroups[week];
      const debit = weekData.reduce((s, r) => s + (r.debit || 0), 0);
      const credit = weekData.reduce((s, r) => s + (r.credit || 0), 0);
      return {
        Week: `Week ${week}`,
        Total_Debit: debit,
        Total_Credit: credit,
        Balance: debit - credit,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(weeklySummary);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Weekly Summary");

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `Weekly_Report_${formData["Party Name"] || "All"}_${new Date()
        .toISOString()
        .split("T")[0]}.xlsx`
    );
  };

  const inputBgFor = (label) => {
    if (label === "Party Name") return "bg-[#f8d8b4]";
    if (label === "Party Code No") return "bg-[#cfc8f9]";
    if (label.includes("Date")) return "bg-white";
    return "bg-[#fefde6]";
  };

  const handleButtonClick = (label) => {
    switch (label) {
      case "Report":
        exportToExcel();
        break;
      case "Weekly":
        exportWeeklyReport();
        break;
      default:
        fetchTransactions();
        break;
    }
  };

  return (
    <div className="min-h-screen w-screen bg-[#d9e3c1] flex items-start justify-center p-2 overflow-hidden">
      <div
        className="w-full max-w-[1150px] shadow-lg border border-[#7a8652]"
        style={{ height: "calc(100vh - 40px)" }}
      >
        <div className="h-8 bg-[#6b7746] text-white flex items-center justify-center px-3 text-xs border-b border-[#4b5a30]">
          Sorathiya Software Amreli 99794 77751 -{" "}
          {new Date().toLocaleDateString("en-GB")}
        </div>

        <div
          className="bg-[#eef3de] border-[4px] border-[#9aa56a] flex"
          style={{ height: "calc(100% - 32px)" }}
        >
          <div className="w-[35%] p-4 border-r-2 border-[#9aa56a] flex flex-col">
            <h2 className="text-lg font-bold text-center mb-1">
              {uiData.title}
            </h2>
            <p className="text-xs text-center mb-4">{uiData.subtitle}</p>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {uiData.fields.map((field, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <label className="text-[#1f1f1f] font-medium mb-1">
                    {field.label} :
                  </label>

                  {field.label === "Party Name" ? (
                    <select
                      onChange={(e) => handleChange("Party Name", e.target.value)}
                      className={`w-[50%] px-2 py-1 border border-[#9aa56a] ${inputBgFor(
                        field.label
                      )} text-sm`}
                      value={formData["Party Name"] ?? ""}
                    >
                      <option>Select Party</option>
                      {Array.isArray(parties) &&
                        parties.map((p) => (
                          <option key={p.id}>{p.party_name}</option>
                        ))}
                    </select>
                  ) : field.label === "Party Code No" ? (
                    <input
                      type="text"
                      value={formData["Party Code No"] ?? ""}
                      readOnly
                      className={`px-2 py-1 border border-[#9aa56a] ${inputBgFor(
                        field.label
                      )} text-sm`}
                    />
                  ) : field.type === "date" ? (
                    <input
                      type="date"
                      value={formData[field.label] ?? ""}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => handleChange(field.label, e.target.value)}
                      className={`px-2 py-1 border border-[#9aa56a] ${inputBgFor(
                        field.label
                      )} text-sm`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData[field.label] ?? ""}
                      onChange={(e) => handleChange(field.label, e.target.value)}
                      className={`px-2 py-1 border border-[#9aa56a] ${inputBgFor(
                        field.label
                      )} text-sm`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-3 justify-between">
              {uiData.buttons.map((btn, i) => (
                <button
                  key={i}
                  onClick={() => handleButtonClick(btn.label)}
                  className="flex-1 bg-[#e8e9e5] border border-[#9aa56a] px-3 py-2 rounded-sm text-sm font-semibold shadow-sm hover:bg-[#f5f5f3]"
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>

          <div className="w-[65%] p-4 flex flex-col">
            <div className="flex-1 border border-[#9aa56a] flex flex-col">
              <div className="grid grid-cols-5 bg-[#f6f5d7] border-b border-[#9aa56a] text-sm font-semibold">
                {uiData.table.columns.map((col, i) => (
                  <div
                    key={i}
                    className="px-2 py-1 border-r border-[#9aa56a] last:border-r-0"
                  >
                    {col}
                  </div>
                ))}
              </div>

              <div className="bg-[#fffce8] flex-1 overflow-y-auto text-sm">
                {transactions.length > 0 ? (
                  transactions.map((row, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-5 border-b border-[#ddd] last:border-b-0"
                    >
                      <div className="px-2 py-1">{row.party_code}</div>
                      <div className="px-2 py-1">{row.party_name}</div>
                      <div className="px-2 py-1">{row.opening_balance}</div>
                      <div className="px-2 py-1">{row.opening_credit}</div>
                      <div className="px-2 py-1">{/* maybe calculated balance */}</div>

                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    No data found
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 text-sm flex justify-between text-[#333] font-semibold">
              <div>Opening: {totals.opening}</div>
              <div>Total Debit: {totals.debit}</div>
              <div>Total Credit: {totals.credit}</div>
              <div>Total Amount: {totals.amount}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartyList;
